import type { GetStaticProps, GetStaticPaths } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import 'twin.macro';

export const config = {
	unstable_includeFiles: ['.next/static/chunks/**/*.js'],
};

export const getStaticPaths: GetStaticPaths = async () => {
	const path = 'static-incremental';
	return {
		paths: [{ params: { slug: path } }],
		fallback: true,
	};
};
export const getStaticProps: GetStaticProps = async ({ params }) => {
	const random = Math.random() * 100;
	const revalidate = 10;
	return { props: { random, revalidate }, revalidate };
};

const StaticPropsPage = ({ random, revalidate }) => {
	const [hydrated, setHydrated] = useState(false);
	useEffect(() => {
		setHydrated(true);
	}, []);

	const router = useRouter();

	// If the page is not yet generated, this will be displayed
	// initially until getStaticProps() finishes running
	if (router.isFallback) {
		return (
			<div tw="text-7xl text-purple-700 leading-tight">
				Oh, people don't ask for this too often, please wait a sec...
			</div>
		);
	}

	return (
		<div tw="max-w-7xl flex justify-center py-16">
			<article tw="prose prose-lg prose-blue max-w-5xl">
				<h1>A Page with getStaticProps + revalidate (ISR)</h1>
				{!hydrated && (
					<p tw="text-red-700">
						If you see me the page hasn't hydrated and is not interactive...
					</p>
				)}
				{hydrated && (
					<p tw="text-green-700">
						If you see me the page has hydrated and is interactive...
					</p>
				)}
				<p>
					Random number from <code>getStaticProps</code>: {random}. Will change
					every
					<code>revalidate</code> interval of {revalidate} seconds on the first
					visitor and then be the same static page for subsequent visitors
					within {revalidate} seconds.
				</p>
				<p>
					It get's prerendered at ... it's too complicated, see at{' '}
					<Link href="https://vercel.com/docs/concepts/next.js/incremental-static-regeneration">
						the concept guide of Vercel
					</Link>
				</p>
				<p>
					Will it work with strict-dynamic + hash? Yes! As long as least 1 page
					get's prerendered at build time.
				</p>
				<p>
					E.g. if you had 1 million product pages in an e-commerce app, as long
					as you prerender at least 1 product page at build-time by returning a
					page path from id in <code>getStaticPaths</code> and set{' '}
					<code>{`{ fallback: true }`}</code>. Then you can prerender the
					remaining 999999 products lazy on demand.
				</p>
				<p>
					It works like this, because script hashes are written to file at build
					time per (dynamic) route (not per page path) and it is needed to have
					write access to the filesystem for that (to write in public folder).
					For lazy page paths with ISR you also can't do things like reading
					Markdown from file system for the same reasons. So the same rules as
					in <code>getServerSideProps</code> seem to count here. Except those
					pages aren't prerendered per request, they are "static for a while".
				</p>
				<h2>Links to other pages</h2>
				<p>Too see if internal navigation works:</p>
				<ul>
					<li>
						<Link href="/csp-test/static">Page with getStaticProps</Link>
					</li>
					<li>
						<Link href="/csp-test/dynamic">Page with getServerSideProps</Link>
					</li>
				</ul>
				{/* <MDXRemote {...mdx} /> */}
			</article>
		</div>
	);
};

export default StaticPropsPage;
